import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './JobTicket.css';

const JobTicket = ({ job, companyName = 'Skip Company' }) => {
    // Format date - always use today's date
    const formatDate = () => {
        const date = new Date();
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Get current date/time for print timestamp
    const printTimestamp = new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

    // Get reference (first 8 chars of Skip ID)
    const reference = job.skipId ? job.skipId.substring(0, 8) : 'N/A';

    // Generate maps URL for QR code
    const generateMapsUrl = () => {
        const lat = job.position?.[0];
        const lng = job.position?.[1];

        // Use Google Maps URL with coordinates - works universally on iOS/Android
        // iOS automatically opens Apple Maps, Android opens Google Maps
        if (lat && lng) {
            return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
        }

        // Fallback to address search if no coordinates
        const address = job.address?.formattedAddress || job.formattedAddress || job.streetAddress;
        if (address) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
        }

        return null;
    };

    return (
        <div className="job-ticket">
            {/* Header */}
            <div className="ticket-header">
                <h1 className="company-name">{companyName}</h1>
                <div className="ticket-type">COLLECTION JOB TICKET</div>
            </div>

            {/* Collection Address - MOST PROMINENT */}
            <div className="collection-address-section">
                <div className="section-label">COLLECTION ADDRESS</div>
                <div className="collection-address">
                    {job.address?.formattedAddress || job.formattedAddress || job.address?.displayAddress || job.streetAddress || 'N/A'}
                </div>
            </div>

            {/* Job Details */}
            <div className="job-details-section">
                <div className="section-label">JOB DETAILS</div>

                <div className="details-grid">
                    <div className="detail-row">
                        <span className="detail-label">Date:</span>
                        <span className="detail-value">{formatDate()}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Skip Size:</span>
                        <span className="detail-value detail-highlight">{job.size || 'N/A'}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Waste Type:</span>
                        <span className="detail-value">{job.wasteType || job.description || 'N/A'}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Job Type:</span>
                        <span className="detail-value">Collection</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Delivered by:</span>
                        <span className="detail-value">{job.driverName || 'Unassigned'}</span>
                    </div>

                    <div className="detail-row">
                        <span className="detail-label">Payment:</span>
                        <span className={`detail-value ${job.paymentStatus === 'TO PAY' ? 'payment-to-pay' : ''}`}>
                            {job.paymentStatus || 'Unknown'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Note */}
            {job.driverNote && (
                <div className="customer-notes-section">
                    <div className="section-label">NOTE</div>
                    <div className="customer-notes">
                        {job.driverNote}
                    </div>
                </div>
            )}

            {/* Signature Section */}
            <div className="signature-section">
                <div className="section-label">CUSTOMER SIGNATURE</div>

                <div className="signature-box">
                    <div className="signature-line">
                        <span className="signature-label">Signature:</span>
                        <div className="signature-line-border"></div>
                    </div>

                    <div className="signature-line">
                        <span className="signature-label">Print Name:</span>
                        <div className="signature-line-border"></div>
                    </div>

                    <div className="signature-line">
                        <span className="signature-label">Date:</span>
                        <div className="signature-line-border signature-line-short">
                            <span style={{
                                position: 'relative',
                                top: '-2px',
                                left: '5px',
                                fontSize: '10pt'
                            }}>
                                {formatDate()}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer - QR Code and Reference */}
            <div className="ticket-footer-qr">
                {generateMapsUrl() && (
                    <div className="qr-section">
                        <QRCodeSVG
                            value={generateMapsUrl()}
                            size={80}
                            level="M"
                            includeMargin={false}
                        />
                        <div className="qr-label">Scan for directions</div>
                    </div>
                )}

                <div className="footer-text">
                    <div className="reference">Ref: {reference}</div>
                    <div className="print-info">Printed: {printTimestamp}</div>
                </div>
            </div>
        </div>
    );
};

export default JobTicket;
